import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Person, EMPTY_PERSON, DOCUMENT_TYPES, STAFF_TAGS } from '@turizi/shared';
import { TextInput, NumberInput, Select, MultiSelect, Button, ActionIcon, Tooltip, CopyButton, Loader } from '@mantine/core';
import { useUpdatePerson, useDeletePerson } from '@/hooks/api-hooks';
import {
    IconMail,
    IconPhone,
    IconMapPin,
    IconId,
    IconBrandWhatsapp,
    IconCheck,
    IconCopy,
    IconTrash
} from '@tabler/icons-react';
import CountrySelector from '@/components/country-selector';
import ShowMetadata from '../show-metadata';
import { PaxCategoryBadge, PaxCategorySelect } from '../snippets';

interface Props {
    person: Person;
}

export default function PersonDetailsTab({ person }: Props): JSX.Element {
    const { t } = useTranslation();
    const [draft, setDraft] = useState<Person>(person || EMPTY_PERSON);
    const [backup, setBackup] = useState<Person>(person || EMPTY_PERSON);
    const updatePersonMutation = useUpdatePerson();
    const deletePersonMutation = useDeletePerson();
    const [savingSections, setSavingSections] = useState<Record<string, boolean>>({});

    const onDelete = async () => {
        if (confirm(t('confirm_delete'))) {
            await deletePersonMutation.mutateAsync(person.id);
            window.location.reload();
        }
    };

    useEffect(() => {
        setDraft(person);
        setBackup(person);
    }, [person]);

    const handleInputChange = (field: keyof Person, value: any) => {
        setDraft(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = (section: string = 'info', updatedDraft?: Person) => {
        const payload = updatedDraft || draft;
        setSavingSections(prev => ({ ...prev, [section]: true }));
        updatePersonMutation.mutate(payload, {
            onSuccess: (data) => {
                setBackup(data.data);
                setDraft(data.data);
                setSavingSections(prev => ({ ...prev, [section]: false }));
            },
            onError: () => {
                setSavingSections(prev => ({ ...prev, [section]: false }));
            }
        });
    };

    const hasInfoChanges = () => {
        return draft.name !== backup.name ||
            draft.category !== backup.category ||
            (draft.adults ?? 0) !== (backup.adults ?? 0) ||
            (draft.children ?? 0) !== (backup.children ?? 0) ||
            (draft.infants ?? 0) !== (backup.infants ?? 0) ||
            (draft.seniors ?? 0) !== (backup.seniors ?? 0) ||
            (draft.guests ?? 0) !== (backup.guests ?? 0) ||
            draft.observations !== backup.observations ||
            draft.phone !== backup.phone ||
            draft.phone_2 !== backup.phone_2 ||
            draft.email !== backup.email ||
            draft.document_type !== backup.document_type ||
            draft.document_number !== backup.document_number ||
            draft.tax_condition !== backup.tax_condition ||
            draft.country_code !== backup.country_code ||
            JSON.stringify(draft.tags) !== JSON.stringify(backup.tags);
    };

    const cleanPhone = draft.phone ? draft.phone.replace(/\D/g, '') : '';
    const cleanPhone2 = draft.phone_2 ? draft.phone_2.replace(/\D/g, '') : '';

    return (
        <div className="py-4">
            {updatePersonMutation.error && (
                <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
                    {updatePersonMutation.error.message}
                </div>
            )}

            <div className="space-y-6 max-w-screen-sm">
                <div className="flex flex-col sm:flex-row gap-4 items-start">
                    <div className="space-y-4 flex-1 w-full min-w-0">
                        <div className="solid-box-border">
                            <div className="space-y-3">
                                <div className="flex gap-1 w-full">
                                    {/* <PaxCategorySelect
                                        size="sm"
                                        label={t('category')}
                                        value={draft.category}
                                        onChange={(value) => handleInputChange('category', value || '')}
                                        className='flex-1'
                                    /> */}
                                    <TextInput
                                        size="sm"
                                        label={t('name')}
                                        value={draft.name || ''}
                                        onChange={(e) => handleInputChange('name', e.target.value)}
                                        className="font-semibold flex-2"
                                    />
                                    <TextInput
                                        size="sm"
                                        label={t('person_notes')}
                                        value={draft.observations || ''}
                                        onChange={(e) => handleInputChange('observations', e.target.value)}
                                        className="flex-2"
                                    />
                                </div>

                                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                                    <NumberInput
                                        size="sm"
                                        label={t('adults')}
                                        min={0}
                                        hideControls
                                        leftSection={<PaxCategoryBadge category="adults" />}
                                        value={draft.adults || ''}
                                        onChange={(val) => handleInputChange('adults', Math.max(0, Number(val) || 0))}
                                        className="font-semibold"
                                    />
                                    <NumberInput
                                        size="sm"
                                        label={t('children')}
                                        min={0}
                                        hideControls
                                        leftSection={<PaxCategoryBadge category="children" />}
                                        value={draft.children || ''}
                                        onChange={(val) => handleInputChange('children', Math.max(0, Number(val) || 0))}
                                        className="font-semibold"
                                    />
                                    <NumberInput
                                        size="sm"
                                        label={t('seniors')}
                                        min={0}
                                        hideControls
                                        leftSection={<PaxCategoryBadge category="seniors" />}
                                        value={draft.seniors || ''}
                                        onChange={(val) => handleInputChange('seniors', Math.max(0, Number(val) || 0))}
                                        className="font-semibold"
                                    />
                                    <NumberInput
                                        size="sm"
                                        label={t('infants')}
                                        min={0}
                                        hideControls
                                        leftSection={<PaxCategoryBadge category="infants" />}
                                        value={draft.infants || ''}
                                        onChange={(val) => handleInputChange('infants', Math.max(0, Number(val) || 0))}
                                        className="font-semibold"
                                    />
                                    <NumberInput
                                        size="sm"
                                        label={t('guests')}
                                        min={0}
                                        hideControls
                                        leftSection={<PaxCategoryBadge category="guests" />}
                                        value={draft.guests || ''}
                                        onChange={(val) => handleInputChange('guests', Math.max(0, Number(val) || 0))}
                                        className="font-semibold"
                                    />
                                </div>

                                <div className="space-y-1">
                                    <TextInput
                                        size="sm"
                                        label={t('phone')}
                                        value={draft.phone || ''}
                                        onChange={(e) => handleInputChange('phone', e.target.value)}
                                        leftSection={<IconPhone size={16} className="text-blue-500" />}
                                        className="font-semibold"
                                        rightSection={
                                            <div className="flex items-center gap-1 pr-1.5">
                                                {draft.phone && (
                                                    <CopyButton value={draft.phone}>
                                                        {({ copied, copy }) => (
                                                            <Tooltip label={copied ? t('copied') : t('copy')} position="top" withArrow>
                                                                <ActionIcon color={copied ? 'teal' : 'gray'} variant="subtle" onClick={copy} size="sm">
                                                                    {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                                                                </ActionIcon>
                                                            </Tooltip>
                                                        )}
                                                    </CopyButton>
                                                )}
                                                {cleanPhone && (
                                                    <Tooltip label={t('chat_on_whatsapp')} position="top" withArrow>
                                                        <ActionIcon
                                                            component="a"
                                                            href={`https://wa.me/${cleanPhone}`}
                                                            target="_blank"
                                                            color="green"
                                                            variant="subtle"
                                                            size="sm"
                                                        >
                                                            <IconBrandWhatsapp size={16} stroke={1.5} />
                                                        </ActionIcon>
                                                    </Tooltip>
                                                )}
                                            </div>
                                        }
                                    />
                                    <TextInput
                                        size="sm"
                                        label={t('phone_2')}
                                        value={draft.phone_2 || ''}
                                        onChange={(e) => handleInputChange('phone_2', e.target.value)}
                                        leftSection={<IconPhone size={16} className="text-blue-500" />}
                                        className="font-semibold"
                                        rightSection={
                                            <div className="flex items-center gap-1 pr-1.5">
                                                {draft.phone_2 && (
                                                    <CopyButton value={draft.phone_2}>
                                                        {({ copied, copy }) => (
                                                            <Tooltip label={copied ? t('copied') : t('copy')} position="top" withArrow>
                                                                <ActionIcon color={copied ? 'teal' : 'gray'} variant="subtle" onClick={copy} size="sm">
                                                                    {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                                                                </ActionIcon>
                                                            </Tooltip>
                                                        )}
                                                    </CopyButton>
                                                )}
                                                {cleanPhone2 && (
                                                    <Tooltip label={t('chat_on_whatsapp')} position="top" withArrow>
                                                        <ActionIcon
                                                            component="a"
                                                            href={`https://wa.me/${cleanPhone2}`}
                                                            target="_blank"
                                                            color="green"
                                                            variant="subtle"
                                                            size="sm"
                                                        >
                                                            <IconBrandWhatsapp size={16} stroke={1.5} />
                                                        </ActionIcon>
                                                    </Tooltip>
                                                )}
                                            </div>
                                        }
                                    />
                                    <TextInput
                                        size="sm"
                                        label={t('email')}
                                        value={draft.email || ''}
                                        onChange={(e) => handleInputChange('email', e.target.value)}
                                        leftSection={<IconMail size={16} className="text-blue-500" />}
                                        rightSection={
                                            draft.email ? (
                                                <CopyButton value={draft.email}>
                                                    {({ copied, copy }) => (
                                                        <Tooltip label={copied ? t('copied') : t('copy')} position="top" withArrow>
                                                            <ActionIcon color={copied ? 'teal' : 'gray'} variant="subtle" onClick={copy} size="sm">
                                                                {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                                                            </ActionIcon>
                                                        </Tooltip>
                                                    )}
                                                </CopyButton>
                                            ) : null
                                        }
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <Select
                                        size="sm"
                                        label={t('id_type')}
                                        value={draft.document_type || ''}
                                        onChange={(value) => handleInputChange('document_type', value)}
                                        data={DOCUMENT_TYPES.map(type => ({ value: type, label: t(type) }))}
                                        leftSection={<IconId size={16} className="text-gray-400" />}
                                    />
                                    <TextInput
                                        size="sm"
                                        label={t('id_number')}
                                        value={draft.document_number || ''}
                                        onChange={(e) => handleInputChange('document_number', e.target.value)}
                                        leftSection={<IconId size={16} className="text-gray-400" />}
                                    />
                                </div>

                                <Select
                                    size="sm"
                                    label={t('tax_condition')}
                                    data={[
                                        { value: '1', label: 'IVA Responsable Inscripto' },
                                        { value: '4', label: 'IVA Sujeto Exento' },
                                        { value: '5', label: 'Consumidor Final' },
                                        { value: '6', label: 'Responsable Monotributo' },
                                        { value: '8', label: 'Sujeto No Categorizado' },
                                        { value: '9', label: 'Proveedor del Exterior' },
                                        { value: '10', label: 'Cliente del Exterior' },
                                        { value: '11', label: 'IVA Liberado - Ley Nº 19.640' },
                                        { value: '13', label: 'Monotributista Social' },
                                        { value: '15', label: 'IVA No Alcanzado' },
                                        { value: '17', label: 'Monotributista Trabajador Independiente Promovido' }
                                    ]}
                                    value={draft.tax_condition || ''}
                                    onChange={(value) => handleInputChange('tax_condition', value)}
                                    clearable
                                />

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <TextInput
                                        size="sm"
                                        label={t('address')}
                                        value={draft.address || ''}
                                        onChange={(e) => handleInputChange('address', e.target.value)}
                                        leftSection={<IconMapPin size={16} className="text-gray-400" />}
                                    />
                                    <CountrySelector
                                        value={draft.country_code || ''}
                                        onChange={(value) => handleInputChange('country_code', value)}
                                        size="sm"
                                    />
                                </div>

                                {person.category === 'staff' && (
                                    <MultiSelect
                                        size="sm"
                                        label={t('tags')}
                                        data={STAFF_TAGS.map(tag => ({ value: tag, label: t(`staff_tag.${tag}`) }))}
                                        value={draft.tags || []}
                                        onChange={(value) => handleInputChange('tags', value)}
                                    />
                                )}
                            </div>

                            <div className="flex justify-end mt-4">
                                <Button
                                    size="xs"
                                    radius="xl"
                                    color="black"
                                    onClick={() => handleSave('info')}
                                    loading={savingSections.info}
                                    disabled={!hasInfoChanges()}
                                >
                                    {t('save')}
                                </Button>
                            </div>
                        </div>

                        <ShowMetadata item={person} />

                        <div className="flex justify-end">
                            <Button
                                color='red'
                                variant='subtle'
                                size='xs'
                                onClick={onDelete}
                                loading={deletePersonMutation.isPending}
                                leftSection={<IconTrash size={18} />}
                            >
                                {t('delete')} {t('person')}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
